import { Injectable, inject } from '@angular/core';
import { AppInfoService } from './app-info.service';
import { io, Socket } from 'socket.io-client';
import { ClsNotificaciones } from '../models/peticiones/cls-pr-presupuestos';
import { ClsUtil } from '../../util/cls-util';
import { Observable } from 'rxjs';
import {environment} from "../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private infoApp = inject(AppInfoService);
  private socket: Socket | null = null;

  public socketStatus = false;
  public presupuestoStatus = false;
  public socketPresupuesto: string;
  public usuario: ClsNotificaciones | null;

  constructor() {
    this.initSocket();
  }

  private initSocket(): void {
    if (this.socket) return;

    this.socket = io(environment.socketUrl, {
      withCredentials: true,
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    this.registerCoreHandlers();
  }

  private registerCoreHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.socketStatus = true;

      if (this.socketPresupuesto && this.usuario && !this.presupuestoStatus) {
        this.socket.emit('join', {
          presupuesto: this.socketPresupuesto,
          usuario: this.usuario,
        });
        this.presupuestoStatus = true;
      }
    });

    this.socket.on('connectionStatus', (data: { recovered: boolean }) => {
      console.log('connectionStatus', data);
      // Aquí podriamos hacer algo si la conexión fue recuperada
    });

    /**
     * razones por las cual puede cerrar la conexion, Mayor info https://socket.io/docs/v4/client-socket-instance/#disconnect
     * io server disconnect = The server has forcefully disconnected the socket with socket.disconnect()
     * io client disconnect = The socket was manually disconnected using socket.disconnect()
     * ping timeout = The server did not send a PING within the pingInterval + pingTimeout range
     * transport close = The connection was closed (example: the user has lost connection, or the network was changed from WiFi to 4G)
     * transport error = The connection has encountered an error (example: the server was killed during a HTTP long-polling cycle)
     */
    this.socket.on('disconnect', (reason: any) => {
      if (reason !== 'transport close') {
        const notif = this.infoApp.principal?.notificacion;
        if (notif?.dataNotificaciones) {
          notif.dataNotificaciones = [];
        }
      }
      // Limpia siempre los usuarios conectados en principal
      if (this.infoApp.principal?.usuariosConectados) {
        this.infoApp.principal.usuariosConectados = [];
      }
      // Evita error en ventana de Login
      const usuarios = this.infoApp.usuarios?.usuariosConectados;
      if (usuarios) {
      this.infoApp.principal.usuariosConectados = [];
      }

      this.socketStatus = false;
      this.presupuestoStatus = false;
    });
  }

  public open(presupuesto: string): void {
    if (!this.socket) {
      this.initSocket();
    }

    this.socketPresupuesto = presupuesto;

    this.usuario = {
      Id: this.infoApp.usuarioLogeado.idUsuario.toString(),
      Name: this.infoApp.usuarioLogeado.name,
      Email: this.infoApp.usuarioLogeado.email,
      Picture: this.infoApp.usuarioLogeado.picture,
    };

    if (this.socket && !this.socket.connected) {
      this.socket.connect();
    }
    if (this.socket && !this.presupuestoStatus) {
      this.socket.emit('join', { presupuesto, usuario: this.usuario });
      this.presupuestoStatus = true;
    }
  }

  public emit(event: string, payload?: any, callback?: Function): void {
    if (!this.socket) return;
    this.socket.emit(event, payload, callback as any);
  }

  public replicate(info: any): void {
    if (!this.socket) return;

    info.Usuario = {
      picture: this.infoApp.usuarioLogeado.picture,
      name: this.infoApp.usuarioLogeado.name,
      email: this.infoApp.usuarioLogeado.email,
    };

    info = ClsUtil.stringifyJSONBig(info);
    this.socket.emit('replicate', {presupuesto: this.socketPresupuesto, info})
  }

  public listen<T = any>(event: string): Observable<T> {
    return new Observable<T>((subscriber) => {
      if (!this.socket) {
        this.initSocket();
      }

      const handler = (data: T) => {
        subscriber.next(data);
      };

      this.socket!.on(event, handler);

      return () => {
        this.socket?.off(event, handler);
      };
    });
  }

  updateUserPartida(nuevaPartida: string): void {
    this.emit('updateUserPartida', {presupuesto: this.socketPresupuesto, nuevaPartida});
  }

  updateUserConcepto(nuevoConcepto: string): void {
    this.emit('updateUserConcepto', {presupuesto: this.socketPresupuesto, nuevoConcepto});
  }

  updateUserMatriz(nuevaMatriz: string): void {
    this.emit('updateUserMatriz', {presupuesto: this.socketPresupuesto, nuevaMatriz});
  }

  public getUsersInBudget(budgetId: string): Promise<ClsNotificaciones[]> {
    return new Promise((resolve, reject) => {
      if (!this.socket) {
        this.initSocket();
      }

      if (!this.socket) {
        return reject('Socket not initialized');
      }

      if (!this.socket.connected) {
        this.socket.connect();
      }

      const payload = { budgetId };

      this.socket.emit(
        'checkBudgetUsers',
        payload,
        (response: {
          ok: boolean;
          users?: ClsNotificaciones[];
          count?: number;
          message?: string;
        }) => {
          if (!response || !response.ok) {
            return reject(response?.message ?? 'Error checking budget users');
          }

          resolve(response.users ?? []);
        }
      );
    });
  }

  public disconnect(): void {
    if (!this.socket) return;
    this.socket.disconnect();
    this.socketPresupuesto = '';
  }

}
